import * as React from 'react';
import { Loader } from 'Src/components/Loader/Loader';
import { ClubProgrammeDataTableShell } from './ClubProgrammeDataTableShell';
import { badgeClass } from '../helpers/clubProgrammeClubsContent';
import {
  fmtDMY,
  getClubDay,
  getClubId,
  getClubSportName,
  getClubStartIso
} from 'Src/helpers/clubProgramme/clubProgramme';
import { getProgrammeClubDayShortLabel, getProgrammeClubGenderLabel } from '../helpers/clubProgrammeClubsContent';

export type AddClubsColumnKey = 'club' | 'sport' | 'day' | 'ages' | 'gender' | 'students' | 'start' | 'notes';

export type AddClubsColumnDefinition = {
  key: AddClubsColumnKey;
  header: string;
  width: number;
  locked?: boolean;
};

export const ADD_CLUBS_COLUMNS: AddClubsColumnDefinition[] = [
  { key: 'club', header: 'Club', width: 340, locked: true },
  { key: 'sport', header: 'Sport', width: 160 },
  { key: 'day', header: 'Day', width: 70 },
  { key: 'ages', header: 'Ages', width: 150 },
  { key: 'gender', header: 'Gender', width: 80 },
  { key: 'students', header: 'Students', width: 110 },
  { key: 'start', header: 'Start', width: 100 },
  { key: 'notes', header: 'Notes', width: 220 }
];

type Props = {
  addItems: any[];
  addMode: 'ELIGIBLE_ONLY' | 'SHOW_ALL';
  canEdit: boolean;
  isAddWorking: boolean;
  addCountsByClubId: { [clubId: string]: { eligible?: number; allow?: number } };
  addCountsLoadingByClubId: { [clubId: string]: boolean };
  addIsSelected: (id: string) => boolean;
  addToggleSelect: (clubId: string) => void;
  getClubAgesLabel: (club: any) => string;
  getClubBadges: (club: any) => any[];
  hiddenColumns: AddClubsColumnKey[];
};

export const ClubProgrammeAddClubsTable: React.FC<Props> = ({
  addItems,
  addMode,
  canEdit,
  isAddWorking,
  addCountsByClubId,
  addCountsLoadingByClubId,
  addIsSelected,
  addToggleSelect,
  getClubAgesLabel,
  getClubBadges,
  hiddenColumns
}) => {
  const hiddenSet = React.useMemo(() => new Set(hiddenColumns || []), [hiddenColumns.join('|')]);
  const visibleColumns = React.useMemo(
    () => ADD_CLUBS_COLUMNS.filter(column => column.locked || !hiddenSet.has(column.key)),
    [hiddenSet]
  );

  const renderCell = (columnKey: AddClubsColumnKey, club: any, rowMeta: any) => {
    switch (columnKey) {
      case 'club':
        return (
          <span className="cpAddClubsClubName" title={rowMeta.clubName}>
            {rowMeta.clubName}
          </span>
        );
      case 'sport':
        return (
          <span className="cpEllipsis" title={rowMeta.sportName}>
            {rowMeta.sportName || '-'}
          </span>
        );
      case 'day':
        return rowMeta.day || '-';
      case 'ages':
        return (
          <span className="cpEllipsis" title={rowMeta.ages}>
            {rowMeta.ages || '-'}
          </span>
        );
      case 'gender':
        return rowMeta.gender || '-';
      case 'students': {
        const clubId = String(rowMeta.id || '');
        const loading = !!addCountsLoadingByClubId[clubId];
        const counts = addCountsByClubId[clubId];

        if (loading) return <span className="text-muted">...</span>;
        if (typeof counts?.eligible === 'number' && typeof counts?.allow === 'number') {
          return (
            <span>
              {counts.allow} / {counts.eligible}
            </span>
          );
        }
        return <span className="text-muted">-</span>;
      }
      case 'start':
        return rowMeta.startDate || '-';
      case 'notes':
        return addMode === 'SHOW_ALL' ? (
          <div className="cpAddClubsBadges">
            {rowMeta.notesBadges.length ? (
              rowMeta.notesBadges.map((badge: any, idx: number) => (
                <span key={`${rowMeta.id}_${idx}`} className={badgeClass(badge.kind)}>
                  {badge.text}
                </span>
              ))
            ) : (
              <span className="text-muted">-</span>
            )}
          </div>
        ) : (
          <span className="text-muted">-</span>
        );
      default:
        return null;
    }
  };

  return (
    <ClubProgrammeDataTableShell>
      {isAddWorking && addItems.length === 0 ? (
        <div className="cpAddClubsLoader">
          <Loader />
        </div>
      ) : (
        <table className="table table-sm cpDataTable cpAddClubsTable">
          <thead>
            <tr>
              <th className="cpAddClubsSelectCol" />
              {visibleColumns.map(column => (
                <th key={column.key} style={{ width: column.width }}>
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {(addItems || []).map((club: any) => {
              const id = getClubId(club);
              const checked = addIsSelected(id);
              const notesBadges =
                addMode === 'SHOW_ALL'
                  ? getClubBadges(club)
                      .filter(b => b.text !== 'Eligible')
                      .slice(0, 3)
                  : [];
              const rowMeta = {
                id,
                clubName: String((club as any).name || ''),
                sportName: getClubSportName(club),
                day: getProgrammeClubDayShortLabel(getClubDay(club)),
                startDate: fmtDMY(getClubStartIso(club)),
                gender: getProgrammeClubGenderLabel(club),
                ages: getClubAgesLabel(club),
                notesBadges
              };

              return (
                <tr
                  key={id}
                  className={[canEdit && !isAddWorking ? 'cpRowClickable' : '', checked ? 'cpRowSelected' : '']
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => {
                    if (!canEdit || isAddWorking) return;
                    addToggleSelect(id);
                  }}
                >
                  <td className="cpAddClubsSelectCol" onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={!canEdit || isAddWorking}
                      onChange={() => addToggleSelect(id)}
                    />
                  </td>

                  {visibleColumns.map(column => (
                    <td key={column.key} style={{ width: column.width, maxWidth: column.width }}>
                      {renderCell(column.key, club, rowMeta)}
                    </td>
                  ))}
                </tr>
              );
            })}

            {!isAddWorking && (addItems || []).length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length + 1} className="text-muted cpAddClubsEmpty">
                  No clubs found for the current filters.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      )}
    </ClubProgrammeDataTableShell>
  );
};
